import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  McpServerResponseSchema,
  McpServerListResponseSchema,
  CreateMcpServerSchema,
  UpdateMcpServerSchema,
  UpsertMcpCredentialSchema,
  McpCredentialResponseSchema,
  McpTestResultSchema,
  OAuthAuthorizeResponseSchema,
  OAuthAuthorizeQuerySchema,
  ApplicationMcpServerListResponseSchema,
  ApplicationMcpServerResponseSchema,
  AttachMcpServerSchema,
  GetMcpServersQuerySchema,
  McpCopilotAvailableServersResponseSchema,
  McpServerIdentityListResponseSchema,
  McpServerIdentitySchema,
  CreateMcpIdentitySchema,
  UpdateMcpIdentitySchema,
  DiscoverMcpOAuthScopesQuerySchema,
  DiscoverMcpServerOAuthScopesQuerySchema,
  McpDiscoveryAuthModelSchema,
} from './mcp-servers.schema';
import { MCP_SERVERS_PATH_NAME } from './mcp-servers.constants';
import { z } from 'zod';
export const McpServersContract = terraContract
  .prefix(`/${MCP_SERVERS_PATH_NAME}`)
  .router({
    listMcpServers: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List MCP servers',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: GetMcpServersQuerySchema }))
      .output(McpServerListResponseSchema),
    createMcpServer: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create a new MCP server',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: CreateMcpServerSchema }))
      .output(McpServerResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    getMcpServer: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}',
        summary: 'Get an MCP server by ID',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(McpServerResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    updateMcpServer: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}',
        summary: 'Update an MCP server',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: UpdateMcpServerSchema,
        }),
      )
      .output(McpServerResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    deleteMcpServer: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}',
        summary: 'Delete an MCP server',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(z.object({ message: z.string() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    upsertMcpCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/credential',
        summary:
          'Create or update static credentials for the default MCP identity',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: UpsertMcpCredentialSchema,
        }),
      )
      .output(McpCredentialResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    listMcpServerIdentities: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/identities',
        summary: 'List auth identities for an MCP server',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(McpServerIdentityListResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    createMcpServerIdentity: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/identities',
        summary: 'Create a new auth identity for an MCP server',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: CreateMcpIdentitySchema,
        }),
      )
      .output(McpServerIdentitySchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    updateMcpServerIdentity: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{id}/identities/{identityId}',
        summary: 'Update an MCP server auth identity',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string(), identityId: z.string() }),
          body: UpdateMcpIdentitySchema,
        }),
      )
      .output(McpServerIdentitySchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    deleteMcpServerIdentity: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}/identities/{identityId}',
        summary: 'Delete an MCP server auth identity',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string(), identityId: z.string() }),
        }),
      )
      .output(z.object({ message: z.string() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    oauthAuthorizeIdentity: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/identities/{identityId}/oauth/authorize',
        summary: 'Get OAuth authorization URL for a specific MCP identity',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string(), identityId: z.string() }),
          query: OAuthAuthorizeQuerySchema,
        }),
      )
      .output(OAuthAuthorizeResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    oauthAuthorize: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/oauth/authorize',
        summary: 'Get OAuth authorization URL for an MCP server',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          query: OAuthAuthorizeQuerySchema,
        }),
      )
      .output(OAuthAuthorizeResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    discoverMcpOAuthScopes: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/oauth/discover-scopes',
        summary:
          'Discover OAuth scopes supported by an MCP server from its metadata',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: DiscoverMcpOAuthScopesQuerySchema }))
      .output(McpDiscoveryAuthModelSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    discoverMcpServerOAuthScopes: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/oauth/discover-scopes',
        summary:
          'Discover OAuth scopes for a managed MCP server, including persisted metadata',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          query: DiscoverMcpServerOAuthScopesQuerySchema,
        }),
      )
      .output(McpDiscoveryAuthModelSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    testMcpServer: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/test',
        summary: 'Test MCP server connectivity and discover tools',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(McpTestResultSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    getMcpServerTools: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/tools',
        summary: 'Get cached tools for an MCP server',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(
        z.object({
          tools: z.array(
            z.object({
              name: z.string(),
              description: z.string().nullable(),
            }),
          ),
        }),
      )
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    getMcpServerPrompts: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/prompts',
        summary: 'Get available prompts for an MCP server',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(
        z.object({
          prompts: z.array(
            z.object({
              name: z.string(),
              description: z.string().nullable(),
            }),
          ),
        }),
      )
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    listApplicationMcpServers: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/applications/{applicationId}/mcp-servers',
        summary: 'List MCP servers attached to an application',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ applicationId: z.string() }) }))
      .output(ApplicationMcpServerListResponseSchema),
    listCopilotAvailableMcpServers: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'GET',
        path: '/copilot/available',
        summary:
          'List tenant MCPs available for the current user to opt into for co-pilot',
        inputStructure: 'detailed',
      })
      .output(McpCopilotAvailableServersResponseSchema),
    optInToCopilotMcp: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'POST',
        path: '/copilot/opt-in/{mcpServerId}',
        summary: 'Opt current user into a tenant MCP for their co-pilot',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ mcpServerId: z.string() }) }))
      .output(z.object({ optedIn: z.boolean() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    optOutFromCopilotMcp: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'DELETE',
        path: '/copilot/opt-in/{mcpServerId}',
        summary: 'Opt current user out of a tenant MCP for their co-pilot',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ mcpServerId: z.string() }) }))
      .output(z.object({ optedIn: z.boolean() })),
    attachMcpServerToApplication: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/applications/{applicationId}/mcp-servers/{mcpServerId}',
        summary: 'Attach an MCP server to an application',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            applicationId: z.string(),
            mcpServerId: z.string(),
          }),
          body: AttachMcpServerSchema,
        }),
      )
      .output(ApplicationMcpServerResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    detachMcpServerFromApplication: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/applications/{applicationId}/mcp-servers/{mcpServerId}',
        summary: 'Detach an MCP server from an application',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            applicationId: z.string(),
            mcpServerId: z.string(),
          }),
        }),
      )
      .output(z.object({ message: z.string() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
  });
